/**
 * Centralized rules for Nexset actions - handles both menu items and action buttons
 *
 * This file manages:
 * - Menu actions: edit, duplicate, delete, pause, activate, copyTransform, pasteTransform, addMarketplace
 * - Button actions: transform, send, split
 * - Node type constraints, flow type constraints, and state-based filtering
 */

interface ActionContext {
  isFullScreenCanvas?: boolean;
  nodeType: string;
  flowType?: string;
  resourceStatus?: string;
  isDetected?: boolean;
  isManaged?: boolean;
  hasChildren?: boolean;
  readOnly?: boolean;
  isSingleDestinationChainFlow?: boolean;
  isSingleChainFlow?: boolean;
  isCapsuleCluster?: boolean;
  disableCopying?: boolean;
  selectActions?: string[];
  isMarketplaceEligible?: boolean;
  [key: string]: any;
}

interface CategorizedActions {
  menu: string[];
  buttons: string[];
  flow: string[];
}

export type ActionId =
  // menu actions
  | 'edit'
  | 'duplicate'
  | 'delete'
  | 'pause'
  | 'activate'
  | 'copyTransform'
  | 'pasteTransform'
  | 'addMarketplace'
  // button actions
  | 'transform'
  | 'send'
  | 'split'
  // flow actions
  | 'activateFlow'
  | 'pauseFlow'
  | 'deleteFlow';

export const ACTION_CATEGORIES = {
  menu: [
    'edit',
    'duplicate',
    'delete',
    'pause',
    'activate',
    'copyTransform',
    'pasteTransform',
    'addMarketplace',
  ] as const,
  buttons: ['transform', 'send', 'split'] as const,
  flow: ['activateFlow', 'pauseFlow', 'deleteFlow'] as const,
} as const;

// default actions for all nexsets
const ALL_POSSIBLE_ACTIONS: ActionId[] = [
  // menu actions
  'pause',
  'activate',
  'duplicate',
  'edit',
  'copyTransform',
  'pasteTransform',
  'delete',
  'addMarketplace',
  // button actions
  'transform',
  'send',
  'split',
  // flow actions
  'activateFlow',
  'pauseFlow',
  'deleteFlow',
];

/**
 * Node type restrictions - only specify what should be REMOVED for special cases
 * Most node types get everything by default, only exceptions need configuration
 */
export const DISALLOWED_ACTIONS_BY_NODE_TYPE: Record<string, ActionId[]> = {
  api_request: [
    'edit',
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
  api_result: [
    'pause',
    'activate',
    'delete',
    'edit',
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
  api_error: [
    'pause',
    'activate',
    'delete',
    'edit',
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
  api_success_modifier: [
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
  splitter_output: [
    'edit',
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'delete',
    'pause',
    'activate',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
  splitter: [
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'split',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
  re_ranker: [
    'duplicate',
    'copyTransform',
    'pasteTransform',
    'transform',
    'send',
    'split',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
};
export function isActionAllowedForNodeType(
  nodeType: string | undefined,
  action: ActionId,
): boolean {
  if (!nodeType) return true;

  const disallowed = DISALLOWED_ACTIONS_BY_NODE_TYPE[nodeType];
  return !disallowed || !disallowed.includes(action);
}

function getBaseActionsForNodeType(nodeType: string): ActionId[] {
  const disallowed = DISALLOWED_ACTIONS_BY_NODE_TYPE[nodeType] || [];
  return ALL_POSSIBLE_ACTIONS.filter((action) => !disallowed.includes(action));
}

const FLOW_TYPE_RESTRICTIONS: Record<string, ActionId[]> = {
  cdc: ['split'],
  elt: ['split'],
  replication: ['transform', 'split'],
  custom: ['transform', 'split'],
  api_server: ['transform', 'send'],
  in_memory: ['split'],
  rag: [
    'transform',
    'send',
    'split',
    'pause',
    'activate',
    'duplicate',
    'edit',
    'copyTransform',
    'pasteTransform',
    'delete',
    'transform',
    'send',
    'split',
    'activateFlow',
    'pauseFlow',
    'deleteFlow',
  ],
};

const NODE_TYPE_RESTRICTIONS: Record<string, ActionId[]> = {
  splitter: ['transform', 'send', 'split'],
};

// apply flow type and additional node type constraints
function applyFlowAndNodeConstraints(actions: ActionId[], context: any): ActionId[] {
  const { flowType, nodeType } = context;

  const restrictedActions = [
    ...(FLOW_TYPE_RESTRICTIONS[flowType] || []),
    ...(NODE_TYPE_RESTRICTIONS[nodeType] || []),
  ];

  return actions.filter((action) => !restrictedActions.includes(action));
}

// apply state-based filtering to actions based on current resource state
function applyStateConstraints(actions: ActionId[], context: any): ActionId[] {
  const {
    flowType,
    resourceStatus,
    isDetected,
    isManaged,
    hasChildren,
    readOnly,
    isSingleDestinationChainFlow,
    isSingleChainFlow,
    isCapsuleCluster,
    disableCopying,
    isMarketplaceEligible,
  } = context;

  return actions.filter((action) => {
    // An API Service node already connected to its next execution node cannot
    // gain a sibling splitter. Only the splitter itself may branch.
    if (flowType === 'api_server' && hasChildren && action === 'split') {
      return false;
    }

    // Detected nexsets restrictions
    if (isDetected && ['copyTransform'].includes(action)) {
      return false;
    }

    if (isDetected && action === 'edit') return false; // detected nexsets cannot be edited

    // Managed nexsets cannot be edited
    if (isManaged && action === 'edit') {
      return false;
    }

    // Single destination chain flows with children cannot send
    if (hasChildren && isSingleDestinationChainFlow && ['send'].includes(action)) {
      return false;
    }

    // Single chain flows with children cannot transform
    if (hasChildren && isSingleChainFlow && ['transform'].includes(action)) {
      return false;
    }

    // Capsule cluster nexsets cannot split (no branching allowed)
    if (isCapsuleCluster && action === 'split') {
      return false;
    }

    // Capsule cluster nexsets with children cannot send (no additional destinations)
    if (hasChildren && isCapsuleCluster && action === 'send') {
      return false;
    }

    // Capsule cluster nexsets with children cannot transform (no branching)
    if (hasChildren && isCapsuleCluster && action === 'transform') {
      return false;
    }

    // capsule cluster nexsets do not expose node-level pause/activate and (flow-level Pause Flow / Activate Flow remain available)
    if (isCapsuleCluster && (action === 'pause' || action === 'activate')) return false;

    // Status action constraints
    if (action === 'pause' && resourceStatus !== 'ACTIVE') {
      return false;
    }

    if (action === 'activate' && resourceStatus === 'ACTIVE') {
      return false;
    }

    // Marketplace eligibility constraint
    if (action === 'addMarketplace' && !isMarketplaceEligible) {
      return false;
    }

    return true;
  });
}

// main function - determines available actions based on context
export function getAvailableActions(context: ActionContext): CategorizedActions {
  const { nodeType, selectActions, isFullScreenCanvas } = context;

  // selectActions narrows the action set, but state constraints remain sovereign.
  if (selectActions) {
    const filtered = applyStateConstraints(selectActions as ActionId[], context).filter(
      (action) =>
        !(isFullScreenCanvas && action === 'deleteFlow') &&
        (nodeType !== 'splitter_output' || isActionAllowedForNodeType(nodeType, action)),
    );

    return {
      menu: filtered.filter((action) => ACTION_CATEGORIES.menu.includes(action as any)),
      buttons: filtered.filter((action) => ACTION_CATEGORIES.buttons.includes(action as any)),
      flow: filtered.filter((action) => ACTION_CATEGORIES.flow.includes(action as any)),
    };
  }

  // default logic
  // Step 1: Get base actions for node type
  let actions = getBaseActionsForNodeType(nodeType);

  // Step 2: Apply flow type and additional node constraints
  actions = applyFlowAndNodeConstraints(actions, context);

  // Step 3: Apply state-based filtering
  actions = applyStateConstraints(actions, context);

  // Step 4: Apply full screen canvas constraints (disallow delete flow)
  actions = actions.filter((action) => !(isFullScreenCanvas && action === 'deleteFlow'));

  // Categorize actions for component consumption
  return {
    menu: actions.filter((action) => ACTION_CATEGORIES.menu.includes(action as any)),
    buttons: actions.filter((action) => ACTION_CATEGORIES.buttons.includes(action as any)),
    flow: actions.filter((action) => ACTION_CATEGORIES.flow.includes(action as any)),
  };
}
